import React from 'react';
import { Brain, Blocks, CreditCard, Building, ArrowRight, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';

const Services = ({ currentLang }) => {
  const t = mockData.translations[currentLang];

  const getIcon = (iconName) => {
    const icons = {
      Brain: Brain,
      Blocks: Blocks,
      CreditCard: CreditCard,
      Building: Building
    };
    return icons[iconName] || Brain;
  };

  return (
    <div className="bg-black min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              Investment <span className="text-[#00FFD1]">Focus Areas</span>
            </h1>
            <p className="text-xl text-white/85 leading-relaxed">
              Specialized investments in cutting-edge technologies that drive digital transformation across Southeast Asia
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="grid lg:grid-cols-2 gap-12">
            {mockData.services.map((service, index) => {
              const IconComponent = getIcon(service.icon);
              return (
                <div key={index} className="bg-white/5 border border-white/20 p-8 hover:bg-white/10 transition-all duration-400 group">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#00FFD1] group-hover:text-black transition-all duration-400">
                      <IconComponent className="w-8 h-8 text-[#00FFD1] group-hover:text-black" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#00FFD1] transition-colors duration-400">
                        {service.title}
                      </h3>
                      <p className="text-white/85 leading-relaxed mb-6">
                        {service.description}
                      </p>
                      
                      {/* Key Investment Areas */}
                      <div className="space-y-2">
                        <h4 className="text-white font-semibold mb-3">Key Investment Areas:</h4>
                        {index === 0 && (
                          <ul className="text-white/70 space-y-1">
                            <li>• Machine Learning Platforms</li>
                            <li>• Computer Vision Solutions</li>
                            <li>• Natural Language Processing</li>
                            <li>• Predictive Analytics Tools</li>
                          </ul>
                        )}
                        {index === 1 && (
                          <ul className="text-white/70 space-y-1">
                            <li>• DeFi Protocols</li>
                            <li>• Asset Tokenization Platforms</li>
                            <li>• Cryptocurrency Exchanges</li>
                            <li>• Smart Contract Infrastructure</li>
                          </ul>
                        )}
                        {index === 2 && (
                          <ul className="text-white/70 space-y-1">
                            <li>• Digital Payment Systems</li>
                            <li>• Mobile Banking Solutions</li>
                            <li>• Microfinance Platforms</li>
                            <li>• Insurance Technology</li>
                          </ul>
                        )}
                        {index === 3 && (
                          <ul className="text-white/70 space-y-1">
                            <li>• IoT Infrastructure</li>
                            <li>• Smart City Solutions</li>
                            <li>• Industrial Automation</li>
                            <li>• Energy Management Systems</li>
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">Our Investment Approach</h2>
            <p className="text-xl text-white/70">
              Strategic partnership beyond capital
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-[#00FFD1]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Growth Partnership</h3>
              <p className="text-white/70">
                We partner with companies at inflection points, providing capital and expertise to accelerate growth.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                <Brain className="w-8 h-8 text-[#00FFD1]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Strategic Guidance</h3>
              <p className="text-white/70">
                Our team provides operational expertise, market insights, and strategic guidance to maximize value creation.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                <Blocks className="w-8 h-8 text-[#00FFD1]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Network Access</h3>
              <p className="text-white/70">
                Portfolio companies gain access to our extensive network of industry leaders, customers, and partners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Partner With Us?</h2>
            <p className="text-xl text-white/70 mb-8">
              Discover how Nusantara Capital.ai can accelerate your company's growth
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/90 border-none rounded-none py-4 px-8 text-lg font-medium min-h-[56px]"
              >
                <Link to="/investor" className="flex items-center gap-3">
                  View Investment Opportunities
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
              
              <Button 
                asChild
                variant="outline"
                className="bg-white/10 text-white border-none rounded-none py-4 px-8 text-lg font-medium min-h-[56px] hover:bg-white hover:text-black"
              >
                <Link to="/contact">
                  {t.buttons.contactUs}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;